import React from "react";
import { Button, ClassNameProps, Column, Grid, makeClassName, Modal, Row, StyleProps } from "@hex-insights/core";
import { ModelUtilities } from "../../../Utilities";
import { PaymentStatusIcon } from "../../_General/PaymentStatus";
import { ContractInstallmentsListModal, ContractInstallmentsListModalProps } from "../ListModal";
import styles from "./styles.module.css";

const responsiveGridTracks = {
	0: 1,
	300: 2,
	600: 4,
};

export type ContractInstallmentsPaymentStatusSummaryButtonGridProps = {
	paidContractInstallments: ContractInstallmentsPaymentStatusSummaryButtonProps["contractInstallments"];
	overdueContractInstallments: ContractInstallmentsPaymentStatusSummaryButtonProps["contractInstallments"];
	dueContractInstallments: ContractInstallmentsPaymentStatusSummaryButtonProps["contractInstallments"];
	upcomingContractInstallments: ContractInstallmentsPaymentStatusSummaryButtonProps["contractInstallments"];
} & Omit<ContractInstallmentsPaymentStatusSummaryButtonProps, "paymentStatus" | "contractInstallments"> &
	Partial<ClassNameProps & StyleProps>;

export function ContractInstallmentsPaymentStatusSummaryButtonGrid({
	paidContractInstallments,
	overdueContractInstallments,
	dueContractInstallments,
	upcomingContractInstallments,
	className,
	style,
	...props
}: ContractInstallmentsPaymentStatusSummaryButtonGridProps) {
	return (
		<Grid responsiveTracks={responsiveGridTracks} gap="1rem" className={className} style={style}>
			<ContractInstallmentsPaymentStatusSummaryButton
				paymentStatus={ModelUtilities.PaymentStatus.Paid}
				contractInstallments={paidContractInstallments}
				noPaymentCreate
				className={styles["grid__button"]}
				{...props}
			/>
			<ContractInstallmentsPaymentStatusSummaryButton
				paymentStatus={ModelUtilities.PaymentStatus.Overdue}
				contractInstallments={overdueContractInstallments}
				className={styles["grid__button"]}
				{...props}
			/>
			<ContractInstallmentsPaymentStatusSummaryButton
				paymentStatus={ModelUtilities.PaymentStatus.Due}
				contractInstallments={dueContractInstallments}
				className={styles["grid__button"]}
				{...props}
			/>
			<ContractInstallmentsPaymentStatusSummaryButton
				paymentStatus={ModelUtilities.PaymentStatus.Upcoming}
				contractInstallments={upcomingContractInstallments}
				className={styles["grid__button"]}
				{...props}
			/>
		</Grid>
	);
}

export type ContractInstallmentsPaymentStatusSummaryButtonProps = {
	paymentStatus: ModelUtilities.PaymentStatus;
} & Omit<ContractInstallmentsListModalProps, "title" | "ifRef" | "onClose" | "className" | "style"> &
	Partial<ClassNameProps & StyleProps>;

export function ContractInstallmentsPaymentStatusSummaryButton({
	paymentStatus,
	contractInstallments,
	className,
	style,
	...props
}: ContractInstallmentsPaymentStatusSummaryButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className={makeClassName(styles["button"], "button--tile", className)} style={style}>
				<Column justify="spaced-start" verticalSpacing="0.5rem">
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<PaymentStatusIcon status={paymentStatus} size={20} />
						<span className={styles["button__payment-status"]}>{paymentStatus}</span>
					</Row>

					<span className={styles["button__count"]}>{contractInstallments.length}</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<ContractInstallmentsListModal
					title={paymentStatus}
					contractInstallments={contractInstallments}
					{...props}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
